import { ForemanEmployeeDispatch, ForemanJobDetail } from '@ups/xplat/api/dto';
// eslint-disable-next-line @nx/enforce-module-boundaries -- direct guid path: the `@ups/xplat/core` barrel reaches MyHttpClientFactory -> @angular/common/http, which needs the JIT compiler under the NativeScript Vitest setup and silently zeroes out every spec transitively importing this file
import { Guid } from '../../../../../core/src/lib/models/base/guid.model';

const SELF_ENTRY_STATUSES = new Set(['Self-Entry', 'Individual-Entry']);

function normalizeSpartaEmployeeId(value: string | null | undefined): string {
  return String(value || '')
    .trim()
    .toLowerCase();
}

/** Resolves Sparta person id whether the API nested `employee` or flattened `employeeID` on the dispatch. */
function getDispatchEmployeeIdForMatching(
  e: ForemanEmployeeDispatch
): string | null | undefined {
  const nested = e.employee?.employeeID;
  if (nested != null && String(nested).trim() !== '') {
    return nested;
  }
  const root = e.employeeID;
  if (root != null && String(root).trim() !== '') {
    return root;
  }
  return undefined;
}

/**
 * Client-side identity guard for the ITE target resolver (#33949). The client's
 * only authoritative person key is the Sparta EmployeeID (these DTOs carry no
 * HRRef and no CreatedBy; name / last4SSN are too collision-prone for a payroll
 * write). A row is the caller's only when its EmployeeID matches, or when it is
 * UNATTRIBUTED (no EmployeeID: the synthetic self-entry / pin-only case, which
 * has no identity to contradict). A row with a DIFFERENT EmployeeID is rejected —
 * that was the bug (a coworker's row summed into the caller's totals). Accepted
 * trade-off: a multi-company caller's own different-company row is excluded until
 * the durable requesterEmployeeIds fix surfaces the set to the client (#30177,
 * #33953).
 */
function rowIdentityMatchesCallerOrIsUnattributed(
  employee: ForemanEmployeeDispatch | null | undefined,
  spartaEmployeeId: string | null | undefined
): boolean {
  if (!employee) {
    return false;
  }
  const rowId = normalizeSpartaEmployeeId(
    getDispatchEmployeeIdForMatching(employee)
  );
  if (!rowId) {
    return true;
  }
  return rowId === normalizeSpartaEmployeeId(spartaEmployeeId);
}

export function isSelfEntryEmployee(
  employee: Partial<ForemanEmployeeDispatch> | null | undefined
): boolean {
  if (!employee || employee.bActive === false) {
    return false;
  }

  const anyEmployee = employee as unknown as {
    employeeJobID?: string | null;
    employeeID?: string | null;
    prospectID?: string | null;
  };

  const isStructuralSelfEntry =
    anyEmployee.employeeJobID == null &&
    anyEmployee.employeeID == null &&
    anyEmployee.prospectID == null;

  return (
    SELF_ENTRY_STATUSES.has(String(employee.dispatchStatus || '')) ||
    isStructuralSelfEntry
  );
}

/**
 * True when a dispatch row carries at least one live labor.
 *
 * Used ONLY as a last-resort tie-break below. Duplicate self-entry rows for one
 * person are a real condition on this array (#30177): the store has no
 * dedupe-on-write and two different job-identity functions feed it, so a second,
 * EMPTY row can sort ahead of the row holding the entered hours. Both resolvers
 * below previously took the first match positionally, which made those hours
 * unreachable to every consumer at once — job row, day total, weekly card and
 * the submit gate all resolve through here, so they agreed on zero and it read
 * as lost time rather than as a display bug.
 *
 * Deliberately date-agnostic: the resolvers have no date context, and picking a
 * row that holds SOME time beats picking one that holds none. This is a
 * mitigation, not the cure — the duplicate should not exist. See #30176.
 */
function dispatchHasAnyLabor(
  employee: ForemanEmployeeDispatch | null | undefined
): boolean {
  return (employee?.labors || []).some((labor) => labor?.bActive !== false);
}

/** Normalized `first|last` for a self-entry row, or '' when no name is present. */
function selfEntryNameKey(e: ForemanEmployeeDispatch): string {
  const first = String(e.firstName ?? e.employee?.firstName ?? '')
    .trim()
    .toLowerCase();
  const last = String(e.lastName ?? e.employee?.lastName ?? '')
    .trim()
    .toLowerCase();
  return first || last ? `${first}|${last}` : '';
}

/**
 * True when two self-entry rows belong to the same person. Two rows that BOTH
 * carry a Sparta id are the same person iff the ids match (a namesake with a
 * different id is a different person). When at least one row has no id — the
 * #30177 mixed-provenance case, where one duplicate was minted with the profile's
 * `employeeID` (WI #30451) and another is a raw null-id row echoed back from the
 * server — fall back to the resolved name. This keeps the id/name split that let a
 * single person's duplicates escape a plain keyed dedupe from reopening the bug.
 */
function selfEntryRowsShareIdentity(
  a: ForemanEmployeeDispatch,
  b: ForemanEmployeeDispatch
): boolean {
  const idA = normalizeSpartaEmployeeId(getDispatchEmployeeIdForMatching(a));
  const idB = normalizeSpartaEmployeeId(getDispatchEmployeeIdForMatching(b));
  if (idA && idB) {
    return idA === idB;
  }
  const nameA = selfEntryNameKey(a);
  const nameB = selfEntryNameKey(b);
  return !!nameA && nameA === nameB;
}

/**
 * True when a self-entry row carries anything worth keeping: an active labor, a
 * server-authoritative `spartaLabors` entry with hours, a nonzero recomputed
 * total, or a default work-order phase selection.
 *
 * Deliberately BROADER than `dispatchHasAnyLabor` (which the resolvers use for a
 * labors-only tie-break): a row can hold recorded time in `spartaLabors` /
 * `totalHoursWorked` with an empty `labors` array (server-populated, read
 * independently into the WO summary), and a row can hold the user's default WO
 * pick in `foremanPhaseIDs` before any hours are entered. Dropping either would
 * lose real state, so all of them count as content for the never-drop rule below.
 */
function selfEntryRowHasContent(e: ForemanEmployeeDispatch): boolean {
  if (dispatchHasAnyLabor(e)) {
    return true;
  }
  if ((e?.spartaLabors || []).some((labor) => Number(labor?.hours || 0) > 0)) {
    return true;
  }
  if (Number(e?.totalHoursWorked || 0) > 0) {
    return true;
  }
  return (e?.foremanPhaseIDs || []).some(
    (id) => String(id ?? '').trim() !== ''
  );
}

/**
 * Write-side producer fix for #30177. Collapse duplicate self-entry rows for one
 * person so the empty phantom rows that accumulate on a job never reach the
 * offline cache or any consumer. Sibling of #30176's `dedupeForemanJobsByIdentity`
 * and, like it, applied on BOTH offline-store write paths — `cacheApiData` (online
 * list refresh) and `mergeSelectedJobIntoPersistedJobs` (the per-`selectedJob.set`
 * snapshot persist) — plus the offline read emit, so a duplicate cannot be written
 * by one path and served by another.
 *
 * Grouping mirrors #30176: union-find over `selfEntryRowsShareIdentity` so a
 * person's mixed id/null-id duplicates collapse together, while two genuinely
 * distinct Sparta ids never merge.
 *
 * The one hard invariant: **never drop a row that carries content** (see
 * `selfEntryRowHasContent` — labor, sparta labor hours, a nonzero total, or a
 * default WO pick). Within a person's group: every content row is kept (two rows
 * that both hold time — a pathological split — are both kept; a recoverable
 * duplicate beats silently deleted time). Content-less rows are dropped when the
 * group has any content row; when the group is entirely empty, exactly one row is
 * kept as the placeholder every consumer expects before time is entered.
 * Non-self-entry roster rows are never touched and keep their positions.
 *
 * Filters rows by reference; it does not clone or mutate any dispatch row.
 */
export function dedupeSelfEntryEmployeeDispatches(
  job: ForemanJobDetail | null | undefined
): ForemanJobDetail {
  const employees = job?.employees || [];

  // Indices of the self-entry rows within `employees`, grouped by person via
  // union-find. `parent` is indexed by position in `selfIdx`.
  const selfIdx = employees
    .map((e, i) => (isSelfEntryEmployee(e) ? i : -1))
    .filter((i) => i >= 0);

  if (selfIdx.length < 2) {
    return { ...(job as ForemanJobDetail), employees: [...employees] };
  }

  const parent = selfIdx.map((_, i) => i);
  const find = (i: number): number => {
    let root = i;
    while (parent[root] !== root) {
      root = parent[root];
    }
    while (parent[i] !== root) {
      const next = parent[i];
      parent[i] = root;
      i = next;
    }
    return root;
  };
  const unite = (i: number, j: number) => {
    const ri = find(i);
    const rj = find(j);
    if (ri !== rj) {
      // Keep the lower index as root so first-appearance order stays stable.
      parent[Math.max(ri, rj)] = Math.min(ri, rj);
    }
  };

  for (let i = 0; i < selfIdx.length; i++) {
    for (let j = i + 1; j < selfIdx.length; j++) {
      if (
        selfEntryRowsShareIdentity(employees[selfIdx[i]], employees[selfIdx[j]])
      ) {
        unite(i, j);
      }
    }
  }

  const groupHasContent = new Map<number, boolean>();
  for (let i = 0; i < selfIdx.length; i++) {
    const root = find(i);
    groupHasContent.set(
      root,
      groupHasContent.get(root) ||
        false ||
        selfEntryRowHasContent(employees[selfIdx[i]])
    );
  }

  const keptPlaceholderRoots = new Set<number>();
  const dropIdx = new Set<number>();
  for (let i = 0; i < selfIdx.length; i++) {
    const employeeIndex = selfIdx[i];
    if (selfEntryRowHasContent(employees[employeeIndex])) {
      continue; // never drop a content row
    }
    const root = find(i);
    if (groupHasContent.get(root)) {
      dropIdx.add(employeeIndex); // a content row already represents this person
      continue;
    }
    if (keptPlaceholderRoots.has(root)) {
      dropIdx.add(employeeIndex); // one empty placeholder is enough
      continue;
    }
    keptPlaceholderRoots.add(root);
  }

  const result = employees.filter((_, i) => !dropIdx.has(i));
  return { ...(job as ForemanJobDetail), employees: result };
}

export function findSelfEntryEmployee(
  job: Partial<ForemanJobDetail> | null | undefined,
  spartaEmployeeId?: string | null
): ForemanEmployeeDispatch | undefined {
  let candidates = (job?.employees || []).filter((employee) =>
    isSelfEntryEmployee(employee)
  );
  // #33949: when a caller id is supplied, drop self-entry rows stamped with a
  // DIFFERENT EmployeeID so a coworker's self-entry row is never returned as the
  // caller's. Omitting the arg (`undefined`) keeps the legacy contract for the
  // other callers of this helper (merge + presence checks).
  if (spartaEmployeeId !== undefined) {
    candidates = candidates.filter((employee) =>
      rowIdentityMatchesCallerOrIsUnattributed(employee, spartaEmployeeId)
    );
  }
  // Prefer the row that actually holds time; see dispatchHasAnyLabor.
  return candidates.find(dispatchHasAnyLabor) ?? candidates[0];
}

/**
 * When the signed-in Sparta employee is already on the job roster (dispatched),
 * return that row — preferring one with a real `employeeJobID`.
 */
export function findEmployeeDispatchMatchingEmployeeId(
  job: Partial<ForemanJobDetail> | null | undefined,
  spartaEmployeeId: string | null | undefined
): ForemanEmployeeDispatch | undefined {
  const want = normalizeSpartaEmployeeId(spartaEmployeeId);
  if (!want) {
    return undefined;
  }

  const matches = (job?.employees || []).filter(
    (e) =>
      normalizeSpartaEmployeeId(getDispatchEmployeeIdForMatching(e)) === want
  );

  if (!matches.length) {
    return undefined;
  }

  // `employeeJobID` still wins: a real dispatched roster row is the right target
  // even when empty. The labor tie-break only decides the cell where NO match
  // has one, which is exactly the duplicate-self-entry case. See #30177.
  return (
    matches.find((e) => !!e.employeeJobID) ??
    matches.find(dispatchHasAnyLabor) ??
    matches[0]
  );
}

/**
 * Employee row the Individual flow edits: dispatched match for the caller, else
 * the caller-scoped self-entry row, else a sole active roster row ONLY when it is
 * the caller's (matching EmployeeID) or unattributed (pin-only / keyed-time).
 * #33949: a sole row with a DIFFERENT EmployeeID is a coworker's as far as the
 * client can tell, so it returns undefined (multi-company own-row #30177 is
 * restored by #33953). Per-branch rationale is in the inline notes below.
 */
export function findIndividualTimeEntryTargetEmployee(
  job: Partial<ForemanJobDetail> | null | undefined,
  spartaEmployeeId?: string | null
): ForemanEmployeeDispatch | undefined {
  const dispatched = findEmployeeDispatchMatchingEmployeeId(
    job,
    spartaEmployeeId
  );
  if (dispatched) {
    return dispatched;
  }

  // #33949: scope the self-entry fallback to the caller. Pass an explicit value
  // (never `undefined`) so the identity filter always runs on this resolver path.
  const selfEntry = findSelfEntryEmployee(job, spartaEmployeeId ?? null);
  if (selfEntry) {
    return selfEntry;
  }

  // A sole active roster row is returned only when it is the caller's (matching
  // EmployeeID) or unattributed (no EmployeeID: pin-only / keyed-time). #33949: a
  // sole row stamped with a DIFFERENT EmployeeID is a coworker's row as far as
  // the client can tell — there is no HRRef to prove it is the caller's own
  // different-company row — so it must not be attributed to the caller. The
  // multi-company own-row case (#30177) is restored by the durable fix #33953.
  const active = (job?.employees || []).filter((e) => e?.bActive !== false);
  if (
    active.length === 1 &&
    spartaEmployeeId &&
    rowIdentityMatchesCallerOrIsUnattributed(active[0], spartaEmployeeId)
  ) {
    return active[0];
  }

  return undefined;
}

/**
 * Shared helpers for Individual Time Entry self-entry rows (web + NativeScript).
 * `createSyntheticSelfEntryEmployee` lives below — keep name/email resolution here
 * so synth and submit validation stay on one path.
 */
function looksLikeSelfEntryEmail(value?: string | null): boolean {
  return String(value ?? '').includes('@');
}

function splitFullName(fullName?: string | null): {
  first: string;
  last: string;
} {
  const parts = String(fullName ?? '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return { first: '', last: '' };
  if (parts.length === 1) return { first: parts[0], last: '' };
  return { first: parts[0], last: parts.slice(1).join(' ') };
}

/**
 * Resolve a submittable First/Last name for a self-entry row from the logged-in
 * profile. Sparta's `ForemanSaveEmployeeDto` validation requires EITHER an
 * `EmployeeID` OR both `LastName` and `FirstName`, so a null-EmployeeID self-entry
 * row MUST carry a real name or submit 400s ("Either 'EmployeeID' or 'LastName',
 * 'FirstName' is required to Save Foreman Employee Record").
 *
 * Mirrors the mobile foreman app's `applyMyInfoToEmployeeDispatchNames`: prefer
 * the profile's first/last, fall back to splitting `fullName`, never send an
 * email as a name (a Security profile can mirror Auth0 with the email as the
 * "first name"), and default to Employee/User so submit never fails on a missing
 * name.
 */
export function resolveSelfEntryNames(profile?: {
  firstName?: string | null;
  lastName?: string | null;
  fullName?: string | null;
}): { firstName: string; lastName: string } {
  const { first, last } = resolveRawSelfEntryName(profile);
  return { firstName: first || 'Employee', lastName: last || 'User' };
}

/**
 * The raw first/last a profile yields BEFORE the Employee/User default, sharing
 * one resolution path with `resolveSelfEntryNames` and `hasResolvableSelfEntryName`
 * so the "did we get a real name" question and the submitted name never diverge.
 */
function resolveRawSelfEntryName(profile?: {
  firstName?: string | null;
  lastName?: string | null;
  fullName?: string | null;
}): { first: string; last: string } {
  const fromFull = splitFullName(profile?.fullName);
  let first =
    (profile?.firstName && !looksLikeSelfEntryEmail(profile.firstName)
      ? profile.firstName
      : ''
    ).trim() || fromFull.first;
  let last = (profile?.lastName ?? '').trim() || fromFull.last;
  if (looksLikeSelfEntryEmail(first)) first = fromFull.first;
  if (looksLikeSelfEntryEmail(last)) last = fromFull.last;
  return { first, last };
}

/**
 * True when the logged-in profile yields a real, submittable name, i.e.
 * `resolveSelfEntryNames` would return something other than the Employee/User
 * default. A self-entry row must NOT be synthesized without one: an unresolved
 * profile (the #30201 failure arms) would otherwise submit a real foreman's time
 * under an unattributed "Employee/User" row with a null EmployeeID, detaching the
 * hours from the person who entered them (#30201 shape D / AC-3). The legitimate
 * case (a resolved profile with a real name but no linked EmployeeID) still
 * synthesizes, because it satisfies this predicate.
 */
export function hasResolvableSelfEntryName(profile?: {
  firstName?: string | null;
  lastName?: string | null;
  fullName?: string | null;
}): boolean {
  const { first, last } = resolveRawSelfEntryName(profile);
  return !!(first || last);
}

/**
 * Build a fresh self-entry target for a user who has neither a dispatched roster
 * row nor an existing self-entry row on the job (e.g. an account with no linked
 * Sparta EmployeeID). The row is structurally self-entry (`employeeJobID`,
 * `manPowerReqDetailID` null; `dispatchStatus` Self-Entry) with a fresh
 * `foremanEmployeeID`. When the profile has a Sparta `employeeID`, stamp it on
 * the row so `ForemanSubmitTimeRepository.SaveEmployee` reuses the linked
 * `ForemanEmployee` (WI #30451) instead of minting an unlinked duplicate.
 *
 * A First/Last name is REQUIRED by server-side validation (see
 * `resolveSelfEntryNames`); pass the logged-in user's profile names so submit
 * does not 400. Defaults to Employee/User when no name is available.
 */
export function createSyntheticSelfEntryEmployee(options?: {
  firstName?: string | null;
  lastName?: string | null;
  fullName?: string | null;
  /** Sparta Employee master id when the profile has one (WI #30451). */
  employeeID?: string | null;
}): ForemanEmployeeDispatch {
  const foremanEmployeeID = Guid.newGuid();
  const { firstName, lastName } = resolveSelfEntryNames(options);
  const employeeID =
    options?.employeeID != null && String(options.employeeID).trim() !== ''
      ? String(options.employeeID)
      : null;

  return {
    foremanEmployeeJobID: Guid.newGuid(),
    employeeJobID: null,
    manPowerReqDetailID: null,
    foremanEmployeeID,
    employeeID,
    prospectID: null,
    firstName,
    lastName,
    shift: 0,
    dispatchStatus: 'Self-Entry',
    dispatchStatusID: null,
    foremanPhaseID: null,
    labors: [],
    bActive: true,
    employee: {
      foremanEmployeeID,
      employeeID,
      firstName,
      lastName,
      bActive: true,
    },
  } as unknown as ForemanEmployeeDispatch;
}
